import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiEye, FiTrash2, FiUpload } from 'react-icons/fi';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ImportShipmentModal from '../../components/ImportShipmentModal';

const OceanExportList = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [etaFilter, setEtaFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [shipperFilter, setShipperFilter] = useState('');
  const [podFilter, setPodFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadShipments();
  }, [statusFilter, etaFilter, customerFilter, shipperFilter, podFilter, searchQuery]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/ocean-export');
      let data = res.data || [];

      if (statusFilter) {
        data = data.filter(s => s.status === statusFilter);
      }
      if (etaFilter) data = data.filter(s => s.eta && s.eta.startsWith(etaFilter));
      if (customerFilter) data = data.filter(s => (s.customer || '').toLowerCase().includes(customerFilter.toLowerCase()));
      if (shipperFilter) data = data.filter(s => (s.shipper || '').toLowerCase().includes(shipperFilter.toLowerCase()));
      if (podFilter) data = data.filter(s => (s.port_of_discharge || '').toLowerCase().includes(podFilter.toLowerCase()));
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        data = data.filter(s =>
          (s.file_no || '').toLowerCase().includes(q) ||
          (s.mbl_no || '').toLowerCase().includes(q) ||
          (s.carrier || '').toLowerCase().includes(q) ||
          (s.vessel || '').toLowerCase().includes(q) ||
          (s.customer || '').toLowerCase().includes(q) ||
          (s.port_of_loading || '').toLowerCase().includes(q) ||
          (s.port_of_discharge || '').toLowerCase().includes(q)
        );
      }
      setShipments(data);
    } catch (error) {
      toast.error('Failed to load shipments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/ocean-export/${id}`);
      toast.success('Shipment deleted');
      setShipments(shipments.filter(s => s.id !== id));
      setConfirmDelete(null);
    } catch (error) {
      toast.error('Failed to delete shipment');
    }
  };

  const columns = [
    {
      key: 'file_no',
      label: 'File No.',
      sortable: true,
      render: (value, row) => (
        <Link
          to={`/ocean-export/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          {value}
        </Link>
      ),
    },
    {
      key: 'mbl_no',
      label: 'MB/L No.',
      sortable: true,
      render: (value, row) => (
        <Link
          to={`/ocean-export/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          {value}
        </Link>
      ),
    },
    { key: 'port_of_loading', label: 'Port of Loading', sortable: true },
    { key: 'port_of_discharge', label: 'Port of Discharge', sortable: true },
    { key: 'carrier', label: 'Carrier', sortable: true },
    { key: 'first_hbl_no', label: 'HB/L No.', sortable: true },
    { key: 'container_qty', label: 'Container/Qty', sortable: false },
    { key: 'vessel', label: 'Vessel', sortable: true },
    { key: 'voyage', label: 'Voyage', sortable: true },
    {
      key: 'package_count', label: 'Package', sortable: true,
      render: (v) => v || '-',
    },
    {
      key: 'weight_kg', label: 'Weight', sortable: true,
      render: (v) => v ? `${Number(v).toLocaleString()} KG` : '-',
    },
    {
      key: 'measurement_cbm', label: 'Measurement', sortable: true,
      render: (v) => v ? `${Number(v).toFixed(2)} CBM` : '-',
    },
    { key: 'customer', label: 'Customer', sortable: true },
    { key: 'op', label: 'OP', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
    { key: 'container_nos', label: 'Container', sortable: true },
  ];

  const renderActions = (row) => [
    <button
      key="view"
      className="btn btn-sm btn-primary"
      onClick={() => navigate(`/ocean-export/${row.id}`)}
      title="View"
    >
      <FiEye /> VIEW
    </button>,
    <button
      key="delete"
      className="btn btn-sm btn-danger"
      onClick={() => setConfirmDelete(row)}
      title="Delete"
    >
      <FiTrash2 />
    </button>,
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">OCEAN EXPORT - MASTER B/L LIST</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn" onClick={() => setShowImport(true)} title="Import from ISF/BOL file">
              <FiUpload /> Import
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/ocean-export/new')}>
              <FiPlus /> New Shipment
            </button>
          </div>
        </div>

        <div className="filter-bar" style={{ flexWrap: 'wrap' }}>
          <div className="filter-group">
            <label className="filter-label">Status:</label>
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="In Transit">In Transit</option>
              <option value="Arrived">Arrived</option>
              <option value="Delivered">Delivered</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">ETA:</label>
            <input type="date" className="filter-input" style={{ width: '150px' }} value={etaFilter} onChange={(e) => setEtaFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Customer:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Shipper:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={shipperFilter} onChange={(e) => setShipperFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Port of Discharge:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={podFilter} onChange={(e) => setPodFilter(e.target.value)} />
          </div>
          <div className="filter-group" style={{ flex: 1, marginLeft: 'auto' }}>
            <input type="text" className="filter-input" placeholder="Search by file no, MB/L, carrier, vessel, customer..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={shipments}
          loading={loading}
          renderActions={renderActions}
          pageSize={15}
        />
      </div>

      {/* Import Modal */}
      <ImportShipmentModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        defaultType="ocean-export"
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete Shipment"
        message={`Are you sure you want to delete shipment ${confirmDelete?.file_no}?`}
        isDangerous
        onConfirm={() => handleDelete(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default OceanExportList;
